// App Constants
export const APP_CONFIG = {
    NAME: 'MangelApp',
    VERSION: '2.0.0',
    DESCRIPTION: 'Construction Management System',
};
// API Constants
export const API_ROUTES = {
    AUTH: {
        LOGIN: '/api/auth/login',
        LOGOUT: '/api/auth/logout',
        REGISTER: '/api/auth/register',
        ME: '/api/auth/me',
        REFRESH: '/api/auth/refresh',
        FORGOT_PASSWORD: '/api/auth/forgot-password',
        RESET_PASSWORD: '/api/auth/reset-password',
        VERIFY_RESET_TOKEN: (token) => `/api/auth/reset-password/${token}`,
        VERIFY_EMAIL: (token) => `/api/auth/verify-email/${token}`,
        RESEND_VERIFICATION: '/api/auth/resend-verification',
        VERIFICATION_STATUS: '/api/auth/verification-status',
        ACCEPT_INVITE: '/api/auth/accept-invite',
        REJECT_INVITE: '/api/auth/reject-invite',
        INVITE: (token) => `/api/auth/invite/${token}`,
        DELETE_ACCOUNT: '/api/auth/delete-account',
    },
    ADMIN_AUTH: {
        LOGIN: '/api/admin-auth/login',
        LOGOUT: '/api/admin-auth/logout',
        ME: '/api/admin-auth/me',
        REFRESH: '/api/admin-auth/refresh',
    },
    BUG_AUTH: {
        LOGIN: '/api/bug-auth/login',
        LOGOUT: '/api/bug-auth/logout',
        ME: '/api/bug-auth/me',
        REFRESH: '/api/bug-auth/refresh',
    },
    PROJECTS: {
        LIST: '/api/projects',
        CREATE: '/api/projects',
        GET: (id) => `/api/projects/${id}`,
        UPDATE: (id) => `/api/projects/${id}`,
        DELETE: (id) => `/api/projects/${id}`,
        MEMBERS: (projectId) => `/api/projects/${projectId}/members`,
        REMOVE_MEMBER: (projectId, memberId) => `/api/projects/${projectId}/members/${memberId}`,
        LEAVE: (projectId) => `/api/projects/${projectId}/members/me`,
    },
    TICKETS: {
        LIST: '/api/tickets',
        CREATE: '/api/tickets',
        GET: (id) => `/api/tickets/${id}`,
        UPDATE: (id) => `/api/tickets/${id}`,
        DELETE: (id) => `/api/tickets/${id}`,
        BY_PROJECT: (projectId) => `/api/tickets/project/${projectId}`,
        LIKED: '/api/tickets/liked',
    },
    TICKET_CARD_IMAGE: {
        VALIDATE_TOKEN: '/api/ticket-card-image/validate-token',
        GENERATE: (ticketId) => `/api/ticket-card-image/generate/${ticketId}`,
    },
    UPLOADS: {
        IMAGE: '/api/uploads/image',
        VOICE: '/api/uploads/voice',
        DOCUMENT: '/api/uploads/document',
    },
    AI: {
        ANALYZE_IMAGE: '/api/ai/analyze-image',
        TRANSCRIBE_VOICE: '/api/ai/transcribe-voice',
    },
    NOTIFICATIONS: {
        LIST: '/api/notifications',
        REGISTER_TOKEN: '/api/notifications/register-token',
        UNREAD_COUNT: '/api/notifications/unread-count',
        READ_ALL: '/api/notifications/read-all',
        READ: (id) => `/api/notifications/${id}/read`,
        DELETE: (id) => `/api/notifications/${id}`,
        DEACTIVATE_TOKEN: (tokenId) => `/api/notifications/token/${tokenId}`,
    },
    REELS: {
        FEED: '/api/reels/feed',
        PUBLICATION: (id) => `/api/reels/publications/${id}`,
        PUBLICATION_LIKE: (id) => `/api/reels/publications/${id}/like`,
        PUBLICATION_SAVE: (id) => `/api/reels/publications/${id}/save`,
        PUBLICATION_COMMENTS: (id) => `/api/reels/publications/${id}/comments`,
        PUBLICATION_COMMENTS_READ: (id) => `/api/reels/publications/${id}/comments/read`,
        MY_LIKES: '/api/reels/my/likes',
        MY_SAVES: '/api/reels/my/saves',
        FOLLOWS: '/api/reels/follows',
        PUBLIC_PROFILE: (userId) => `/api/reels/users/${userId}/profile`,
        INBOX_UNREAD_COUNT: '/api/reels/inbox/unread-count',
        INBOX: '/api/reels/inbox',
        INBOX_PUBLICATIONS: (userId) => `/api/reels/inbox/users/${userId}/publications`,
    },
    PRIORITIES: {
        LIST: '/api/priorities',
        CREATE: '/api/priorities',
        GET: (id) => `/api/priorities/${id}`,
        UPDATE: (id) => `/api/priorities/${id}`,
        DELETE: (id) => `/api/priorities/${id}`,
    },
    PROJECT_STATUSES: {
        LIST: '/api/project-statuses',
        CREATE: '/api/project-statuses',
        GET: (id) => `/api/project-statuses/${id}`,
        UPDATE: (id) => `/api/project-statuses/${id}`,
        DELETE: (id) => `/api/project-statuses/${id}`,
    },
    PROJECT_PRIORITIES: {
        LIST: '/api/project-priorities',
        CREATE: '/api/project-priorities',
        GET: (id) => `/api/project-priorities/${id}`,
        UPDATE: (id) => `/api/project-priorities/${id}`,
        DELETE: (id) => `/api/project-priorities/${id}`,
    },
    CLIENT_TYPES: {
        LIST: '/api/client-types',
    },
    DOCUMENTS: {
        UPLOAD: '/api/documents/upload',
        GET: (id) => `/api/documents/${id}`,
        UPDATE: (id) => `/api/documents/${id}`,
        DELETE: (id) => `/api/documents/${id}`,
        BY_PROJECT: (projectId) => `/api/documents/project/${projectId}`,
        BY_ORGANIZATION: (organizationId) => `/api/documents/organization/${organizationId}`,
        CONNECT: (documentId) => `/api/documents/${documentId}/connect`,
        DISCONNECT: (documentId, projectId) => `/api/documents/${documentId}/connect/${projectId}`,
        CONNECTED_PROJECTS: (documentId) => `/api/documents/${documentId}/projects`,
        PROJECT_CONNECTED: (projectId) => `/api/documents/project/${projectId}/connected`,
        EXPORT_ZIP: (projectId) => `/api/documents/project/${projectId}/export-zip`,
    },
    DASHBOARD_COLORS: {
        LIST: '/api/dashboard-colors',
    },
    // Team Bug Reporting Routes
    BUGS: {
        LIST: '/api/bugs',
        CREATE: '/api/bugs',
        ACCESS: '/api/bugs/access',
        GET: (id) => `/api/bugs/${id}`,
        UPDATE: (id) => `/api/bugs/${id}`,
        DELETE: (id) => `/api/bugs/${id}`,
        PROGRESS: (id) => `/api/bugs/${id}/progress`,
        ATTACHMENTS: (id) => `/api/bugs/${id}/attachments`,
        ATTACHMENT_DELETE: (id, attachmentId) => `/api/bugs/${id}/attachments/${attachmentId}`,
        AI_FIX_PROMPT: (id) => `/api/bugs/${id}/ai-fix-prompt`,
        ADMIN_USERS: '/api/bugs/admin/users',
        ADMIN_USER_DELETE: (userId) => `/api/bugs/admin/users/${userId}`,
    },
    ORGS: {
        MY_ORGANIZATIONS: '/api/orgs/my-organizations',
        ME: '/api/orgs/me',
        EFFECTIVE_PERMISSIONS: '/api/orgs/effective-permissions',
        GET: (orgId) => `/api/orgs/${orgId}`,
        LEAVE: (orgId) => `/api/orgs/${orgId}/members/me`,
        DEMO_SETTINGS: (orgId) => `/api/orgs/${orgId}/demo-projects/settings`,
        DEMO_PROJECTS: (orgId) => `/api/orgs/${orgId}/demo-projects`,
    },
    TICKET_ASSIGNEES: {
        LIST: '/api/ticket-assignees',
        CREATE: '/api/ticket-assignees',
        GET: (id) => `/api/ticket-assignees/${id}`,
        UPDATE: (id) => `/api/ticket-assignees/${id}`,
        DELETE: (id) => `/api/ticket-assignees/${id}`,
        ASSIGN_TO_TICKET: (assigneeId, ticketId) => `/api/ticket-assignees/${assigneeId}/assign/${ticketId}`,
        REMOVE_FROM_TICKET: (assigneeId, ticketId) => `/api/ticket-assignees/${assigneeId}/assign/${ticketId}`,
        BY_TICKET: (ticketId) => `/api/ticket-assignees/ticket/${ticketId}`,
    },
};
// Status Constants
export const TICKET_STATUS = {
    OPEN: 'open',
    IN_PROGRESS: 'in_progress',
    RESOLVED: 'resolved',
    CLOSED: 'closed',
    REJECTED: 'rejected',
};
// Canonical semantic buckets for ticket statuses.
// These values are language-independent and safe for filtering/business logic.
export const TICKET_STATUS_SEMANTIC_TYPES = {
    OPEN: 'open',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    DEFERRED: 'deferred',
    OTHER: 'other',
};
export const PRIORITY_LEVELS = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical',
};
export const USER_ROLES = {
    USER: 'user',
    ADMIN: 'admin',
    SUPER_ADMIN: 'super_admin',
};
export const SNAP_FEED_SCOPES = {
    GLOBAL: 'global',
    LOCAL: 'local',
};
// File Upload Constants
export const UPLOAD_LIMITS = {
    IMAGE_MAX_SIZE: 10 * 1024 * 1024, // 10MB
    VOICE_MAX_SIZE: 25 * 1024 * 1024, // 25MB
    DOCUMENT_MAX_SIZE: 50 * 1024 * 1024, // 50MB
};
export const SUPPORTED_FILE_TYPES = {
    IMAGES: ['image/jpeg', 'image/png', 'image/webp'],
    AUDIO: ['audio/mp3', 'audio/wav', 'audio/ogg'],
    DOCUMENTS: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};
// Subscription Constants
export const SUBSCRIPTION_STATUS = {
    ACTIVE: 'active',
    PAST_DUE: 'past_due',
    CANCELLED: 'cancelled',
    EXPIRED: 'expired',
    REFUNDED: 'refunded',
};
export const SUBSCRIPTION_PROVIDERS = {
    STRIPE: 'stripe',
    APPLE: 'apple',
    GOOGLE: 'google',
};
export const SUBSCRIPTION_PROVIDER_ENVIRONMENTS = {
    PRODUCTION: 'production',
    SANDBOX: 'sandbox',
};
export const SUBSCRIPTION_FEATURES = {
    BASIC_SUPPORT: 'basic_support',
    EMAIL_NOTIFICATIONS: 'email_notifications',
    PRIORITY_SUPPORT: 'priority_support',
    ADVANCED_ANALYTICS: 'advanced_analytics',
    API_ACCESS: 'api_access',
    CUSTOM_BRANDING: 'custom_branding',
    ADVANCED_REPORTING: 'advanced_reporting',
    INTEGRATIONS: 'integrations',
    WHITE_LABEL: 'white_label',
};
export const SUBSCRIPTION_PLANS = {
    FREE: 'Free',
    EXPERT: 'Expert',
    PRO: 'Pro',
    RISE: 'Rise',
};
export const BILLING_INTERVALS = {
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    YEARLY: 'yearly',
};
// Subscription Settings Keys (P0)
export const SUBSCRIPTION_SETTINGS_KEYS = {
    PRORATION_SEAT_CHANGE: 'proration_seat_change',
    PRORATION_PLAN_CHANGE: 'proration_plan_change',
    DEFAULT_CANCELLATION_MODE: 'default_cancellation_mode',
    ALLOW_IMMEDIATE_CANCEL: 'allow_immediate_cancel',
    GRACE_PERIOD_DAYS: 'grace_period_days',
    BLOCK_DOWNGRADE_BELOW_ACTIVE: 'block_downgrade_below_active',
    AUTO_ASSIGN_FREE_PLAN: 'auto_assign_free_plan',
    DEFAULT_FREE_PLAN_NAME: 'default_free_plan_name',
    DEFAULT_WHATSAPP_NOTIFICATIONS: 'default_whatsapp_notifications',
};
// Subscription Settings Default Values (P0)
export const SUBSCRIPTION_SETTINGS_DEFAULTS = {
    [SUBSCRIPTION_SETTINGS_KEYS.PRORATION_SEAT_CHANGE]: 'always_invoice',
    [SUBSCRIPTION_SETTINGS_KEYS.PRORATION_PLAN_CHANGE]: 'always_invoice',
    [SUBSCRIPTION_SETTINGS_KEYS.DEFAULT_CANCELLATION_MODE]: 'at_period_end',
    [SUBSCRIPTION_SETTINGS_KEYS.ALLOW_IMMEDIATE_CANCEL]: 'true',
    [SUBSCRIPTION_SETTINGS_KEYS.GRACE_PERIOD_DAYS]: '7',
    [SUBSCRIPTION_SETTINGS_KEYS.BLOCK_DOWNGRADE_BELOW_ACTIVE]: 'true',
    [SUBSCRIPTION_SETTINGS_KEYS.AUTO_ASSIGN_FREE_PLAN]: 'true',
    [SUBSCRIPTION_SETTINGS_KEYS.DEFAULT_FREE_PLAN_NAME]: 'Free',
    [SUBSCRIPTION_SETTINGS_KEYS.DEFAULT_WHATSAPP_NOTIFICATIONS]: 'false',
};
// Subscription API Routes
export const SUBSCRIPTION_ROUTES = {
    MY_PLAN: '/api/subscriptions/my-plan',
    USAGE: '/api/subscriptions/usage',
    PLANS: '/api/subscriptions/plans',
    UPGRADE: '/api/subscriptions/upgrade',
    CANCEL: '/api/subscriptions/cancel',
    MOBILE_VALIDATE: '/api/subscriptions/mobile/validate',
    MOBILE_RESTORE: '/api/subscriptions/mobile/restore',
    APPLE_NOTIFICATIONS: '/api/subscriptions/apple/notifications',
    GOOGLE_NOTIFICATIONS: '/api/subscriptions/google/notifications',
    ORG_SUBSCRIPTION: (orgId) => `/api/orgs/${orgId}/subscription`,
};
// Stripe API Routes
export const STRIPE_ROUTES = {
    CREATE_PAYMENT_INTENT: '/api/stripe/create-payment-intent',
    CONFIRM_SUBSCRIPTION: '/api/stripe/confirm-subscription',
    CANCEL_SUBSCRIPTION: '/api/stripe/cancel-subscription',
    WEBHOOK: '/api/stripe/webhook',
    SYNC_PRODUCTS: '/api/stripe/sync-products',
    SUBSCRIPTION: '/api/stripe/subscription',
    INVOICES: '/api/stripe/invoices',
};
// Admin Dashboard Routes
export const ADMIN_DASHBOARD_ROUTES = {
    ORGANIZATIONS: {
        LIST: '/api/admin-dashboard/organizations',
        GET: (id) => `/api/admin-dashboard/organizations/${id}`,
        UPDATE: (id) => `/api/admin-dashboard/organizations/${id}`,
        DELETE: (id) => `/api/admin-dashboard/organizations/${id}`,
    },
    USERS: {
        LIST: '/api/admin-dashboard/users',
        GET: (id) => `/api/admin-dashboard/users/${id}`,
        UPDATE: (id) => `/api/admin-dashboard/users/${id}`,
        DELETE: (id) => `/api/admin-dashboard/users/${id}`,
    },
    PROJECTS: {
        LIST: '/api/admin-dashboard/projects',
        GET: (id) => `/api/admin-dashboard/projects/${id}`,
        UPDATE: (id) => `/api/admin-dashboard/projects/${id}`,
        DELETE: (id) => `/api/admin-dashboard/projects/${id}`,
    },
    TICKETS: {
        LIST: '/api/admin-dashboard/tickets',
        GET: (id) => `/api/admin-dashboard/tickets/${id}`,
        UPDATE: (id) => `/api/admin-dashboard/tickets/${id}`,
        DELETE: (id) => `/api/admin-dashboard/tickets/${id}`,
    },
    SUPPORT_MESSAGES: {
        LIST: '/api/admin-dashboard/support-messages',
        GET: (id) => `/api/admin-dashboard/support-messages/${id}`,
        REPLY: (id) => `/api/admin-dashboard/support-messages/${id}/reply`,
        MARK_READ: (id) => `/api/admin-dashboard/support-messages/${id}/read`,
    },
    STATS: {
        GLOBAL: '/api/admin-dashboard/stats',
        ORGANIZATION: (id) => `/api/admin-dashboard/stats/organization/${id}`,
    },
    SUBSCRIPTION_PLANS: {
        LIST: '/api/admin-dashboard/subscription-plans',
        GET: (id) => `/api/admin-dashboard/subscription-plans/${id}`,
        CREATE: '/api/admin-dashboard/subscription-plans',
        UPDATE: (id) => `/api/admin-dashboard/subscription-plans/${id}`,
        DELETE: (id) => `/api/admin-dashboard/subscription-plans/${id}`,
        SYNC_STRIPE: '/api/admin-dashboard/subscription-plans/sync-stripe',
        REORDER: '/api/admin-dashboard/subscription-plans/reorder',
    },
    REVENUE: {
        DASHBOARD: '/api/admin-dashboard/revenue',
    },
};
// GA4 analytics event names and param names (shared contract for web + mobile)
export const ANALYTICS_EVENTS = {
    SCREEN_VIEW: 'screen_view',
    SESSION_START: 'session_start',
    LOGIN_SUCCESS: 'login_success',
    LOGOUT: 'logout',
    PROJECT_OPENED: 'project_opened',
    TICKET_OPENED: 'ticket_opened',
    TICKET_CREATED: 'ticket_created',
    TICKET_UPDATED: 'ticket_updated',
    SETTINGS_OPENED: 'settings_opened',
    NOTIFICATION_OPENED: 'notification_opened',
};
export const ANALYTICS_PARAMS = {
    PLATFORM: 'platform',
    ORGANIZATION_ID: 'organization_id',
    PROJECT_ID: 'project_id',
    TICKET_ID: 'ticket_id',
    USER_ROLE: 'user_role',
};
// System User Constants
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';
// Permission Constants
export * from './permissions.js';
// Annotation Constants
export * from './annotations.js';
// WhatsApp ticket link (env-aware View Ticket URL)
export * from './whatsapp-ticket-link.js';
